import { ButtonCus } from '@/components/common/Button';
import { Checkbox } from '@/components/common/Checkbox';
import { Label } from '@/components/common/Label';
import { Textarea } from '@/components/common/Textarea';
import { DISPPLAY_MOMENT_YMD_HM, ekDomain, ROUTES } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import { OPTION_FORMAT } from '@/types/enums';
import { OptionBookingTable } from '@/types/option';
import { ReservationServiceItem } from '@/types/reservation';
import { formatDateTime, formatDateTimeISOJST } from '@/utils/datetime';
import { isPathEk } from '@/utils/routes';
import { formatCurrency } from '@/utils/formatNumber';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { flowResult } from 'mobx';
import '../../../detail/components/style.scss';
import { ChevronRight } from 'lucide-react';

interface ConfirmationProps {
    completeStep: number,
    back: Function
}

export default observer(function Confirmation({ back }: ConfirmationProps) {
    // hook
    const { t } = useTranslation();
    const { pathname } = useLocation();
    const { id } = useParams();
    const navigate = useNavigate();
    // store
    const { reservationStore: { reservationForUpdate, setObservable, updateReservationInfor }, modalStore: { showAlertModal }
    } = useStore();

    const { initialCost, revisedCost, feeNeedToPay, currentOrder, newOrder, note: currentNote, isSentMail } = reservationForUpdate;
    const { information: { location, bike, payment: { discount } }, details: { dateAndTime, services } } = currentOrder;
    const { additionalServiceCost, dateAndTime: newDateTime, dateAndTimeCost, location: newLocation, bike: newBike, totalCost } = newOrder;
    // state
    const [note, setNote] = useState<string | undefined | null>(currentNote || '');
    const [isCheckSendMail, setCheckSendMail] = useState<boolean>(isSentMail ?? true);

    const { oldAdditionalServices, oldTotalOptionFee }: { oldAdditionalServices: ReservationServiceItem[], oldTotalOptionFee: number } = useMemo(() => {
        let returnServices: ReservationServiceItem[] = [];
        const { option = [], compensation = [], dropOff = [], extraMileage = [] } = services;

        returnServices = [
            ...option.map(item => ({ ...item, format: OPTION_FORMAT.Option })),
            ...compensation.map(item => ({ ...item, format: OPTION_FORMAT.Insurance })),
            ...extraMileage.map(item => ({ ...item, format: OPTION_FORMAT.ExtraMileage })),
            ...dropOff.map(item => ({ ...item, format: OPTION_FORMAT.DropOff }))
        ];

        let totalOption = returnServices.reduce((prev, cur) => {
            if (cur.fee && Number(cur.fee) > 0) {
                prev += Number(cur.fee);
            }

            return prev;
        }, 0);
        return {
            oldAdditionalServices: returnServices,
            oldTotalOptionFee: totalOption
        };
    }, [services]);

    const { newAdditionalServices, newTotalOption }: { newAdditionalServices: OptionBookingTable[], newTotalOption: number } = useMemo(() => {
        let services: OptionBookingTable[] = [];
        const { optionSelect = [], compensation = [], dropOff = [], underTwentyYearsOld = [], extraMileage = [] } = additionalServiceCost || {};

        services = [
            ...optionSelect.map(item => ({ ...item, format: OPTION_FORMAT.Option })),
            ...compensation.map(item => ({ ...item, format: OPTION_FORMAT.Insurance })),
            ...underTwentyYearsOld.map(item => ({ ...item, format: OPTION_FORMAT.Option, isUnder20: true })),
            ...extraMileage.map(item => ({ ...item, format: OPTION_FORMAT.ExtraMileage })),
            ...dropOff.map(item => ({ ...item, format: OPTION_FORMAT.DropOff }))
        ];

        let totalOption = services.reduce((prev, cur) => {
            if (cur.totalPrice && Number(cur.totalPrice) > 0) {
                prev += Number(cur.totalPrice);
            }

            return prev;
        }, 0);
        return {
            newAdditionalServices: services,
            newTotalOption: totalOption
        };
    }, [additionalServiceCost]);

    const oldDiscount: number = useMemo(() => {
        return discount.reduce((prev, cur) => {
            if (cur.discountAmount && Number(cur.discountAmount) > 0) {
                prev += Number(cur.discountAmount);
            }

            return prev;
        }, 0);
    }, [discount]);
    // lifecycle
    useEffect(() => {

    });
    // function
    const onSubmit = async () => {
        let payload = {
            locationId: newLocation.id,
            bikeId: newBike.id,
            additionalServices: newAdditionalServices.filter(service => (Number(service.id) > 0)),
            startTime: formatDateTimeISOJST(newDateTime.startTime),
            endTime: formatDateTimeISOJST(newDateTime.endTime),
            orderId: id,
            isSendMail: isCheckSendMail,
            note
        };
        const result = await flowResult(updateReservationInfor(payload));
        if (result) {
            showAlertModal({
                id: 'update-booking-success',
                type: 'success',
                content: t('messages.update_success'),
                onClickXIcon: () => {
                    if (isPathEk(pathname)) {
                        navigate(`/${ekDomain}${ROUTES.reservationManagement.href}/${ROUTES.reservationDetail.href}/${id}`);
                    } else {
                        navigate(`${ROUTES.reservationManagement.href}/${ROUTES.reservationDetail.href}/${id}`);
                    }
                }
            });
        }
    };

    const backToPrev = () => {
        setObservable('reservationForUpdate', {
            note, isSentMail: isCheckSendMail
        }, { isMergeObject: true });
        back();
    };

    return (
        <div className='flex flex-col gap-3'>
            <div className='flex flex-col gap-2 layout-card'>
                <div className='flex justify-center text-[1.0625rem] text-center text-primaryWeb font-semibold items-center px-2'>
                    {newOrder?.additionalServiceCost?.underTwentyYearsOld?.length > 0 && t('booking_bike.sentences.under_20_ages_money')}
                </div>
                <div className='flex gap-3'>
                    <label className='w-52'>{t('field.initial_cost')}</label>
                    <p>
                        {t('currency.currency_symbol')}
                        {formatCurrency(initialCost) || '0'}
                    </p>
                </div>
                <div className='flex gap-3'>
                    <label className='w-52'>{t('field.revised_cost')}</label>
                    <p>
                        {t('currency.currency_symbol')}
                        {formatCurrency(revisedCost) || '0'}
                    </p>
                </div>
                <div className='flex gap-3'>
                    <label className='w-52'>{t('field.amount_to_pay')}</label>
                    <p>
                        {t('currency.currency_symbol')}
                        {formatCurrency(feeNeedToPay) || '0'}
                    </p>
                </div>
                <div className='flex flex-col gap-2'>
                    <Label>{t('field.memo')}</Label>
                    <Textarea value={note || ''} onChange={e => setNote(e.target.value)} placeholder={t('placeholder.enter')} />
                </div>
            </div>
            <div className='flex flex-col gap-3 layout-card'>
                <h3 className='text-[1.0625rem] font-semibold'>{t('words_title.modification_details')}</h3>
                <div className='overflow-x-auto flex gap-1 h-fit'>
                    <table className='border-collapse w-full h-fit booking-table'>
                        <thead>
                            <tr>
                                <th className='bg-gray-6 p-3 text-white'>{t('words_title.information')}</th>
                                <th className='!w-10'></th>
                                <th className='bg-gray-5 p-3 text-black'>{t('words_title.before_change')}</th>
                                <th className='min-w-12'></th>
                                <th className='bg-customRed p-3 text-white'>{t('words_title.after_change')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className=''>
                                <td className='border-b p-3 font-semibold whitespace-nowrap'>{t('field.date&time')}</td>
                                <td className='min-w-5 border-b'></td>
                                <td className='border-b p-3'>
                                    <div className='flex gap-1'>
                                        <label>
                                            {t('field.rental_date_and_time')}
                                            :
                                        </label>
                                        <span>{dateAndTime.rentalFrom && formatDateTime(dateAndTime.rentalFrom, DISPPLAY_MOMENT_YMD_HM, { useLocalTime: false })}</span>
                                    </div>
                                    <div className='flex gap-1'>
                                        <label>
                                            {t('field.return_date_and_time')}
                                            :
                                        </label>
                                        <span>{dateAndTime.rentalTo && formatDateTime(dateAndTime.rentalTo, DISPPLAY_MOMENT_YMD_HM, { useLocalTime: false })}</span>
                                    </div>
                                </td>
                                <td className='min-w-5'></td>
                                <td className='border-b p-3'>
                                    <div className='flex gap-1'>
                                        <label>
                                            {t('field.rental_date_and_time')}
                                            :
                                        </label>
                                        <span>{newDateTime.startTime && formatDateTime(newDateTime.startTime, DISPPLAY_MOMENT_YMD_HM, { useLocalTime: false })}</span>
                                    </div>
                                    <div className='flex gap-1'>
                                        <label>
                                            {t('field.return_date_and_time')}
                                            :
                                        </label>
                                        <span>{newDateTime.endTime && formatDateTime(newDateTime.endTime, DISPPLAY_MOMENT_YMD_HM, { useLocalTime: false })}</span>
                                    </div>
                                </td>
                            </tr>
                            <tr className=''>
                                <td className='border-b p-3 font-semibold whitespace-nowrap'>{t('field.location_name')}</td>
                                <td className='min-w-5 border-b'></td>
                                <td className='border-b p-3'>
                                    <div className='flex justify-between gap-2'>
                                        <p>{location?.name}</p>
                                        <p className='text-black font-semibold text-fs-mid16'>
                                            <span>
                                                {t('field.fee')}
                                                :
                                                {' '}
                                            </span>
                                            <span>
                                                {t('currency.currency_symbol')}
                                                {formatCurrency(dateAndTime.totalAmount) || '0'}
                                            </span>
                                        </p>
                                    </div>
                                </td>
                                <td className='min-w-5'></td>
                                <td className='border-b p-3'>
                                    <div className='flex justify-between gap-2'>
                                        <p>{newLocation?.name}</p>
                                        <p className='text-black text-fs-mid16 font-semibold'>
                                            <span>
                                                {t('field.fee')}
                                                :
                                                {' '}
                                            </span>
                                            <span>
                                                {t('currency.currency_symbol')}
                                                {formatCurrency(dateAndTimeCost) || '0'}
                                            </span>
                                        </p>
                                    </div>
                                </td>
                            </tr>
                            <tr className=''>
                                <td className='border-b p-3 font-semibold whitespace-nowrap'>{t('field.bike_of_location')}</td>
                                <td className='min-w-5 border-b'></td>
                                <td className='border-b p-3'>{bike?.bikeModel}</td>
                                <td className='min-w-5'></td>
                                <td className='border-b p-3'>{newBike.bikeModel}</td>
                            </tr>
                            <tr className=''>
                                <td className='border-b p-3 font-semibold whitespace-nowrap'>{t('field.select_option')}</td>
                                <td className='min-w-5 border-b'></td>
                                <td className='align-top border-b p-3 min-h-full'>
                                    <table className='border border-collapse text-center min-w-full option-selected'>
                                        <thead>
                                            <tr className='bg-gray-5 text-black'>
                                                <th className='border w-1/2 p-2'>{t('field.option_page.option_name')}</th>
                                                <th className='border w-[15%] p-2'>
                                                    <span
                                                        className='unit-price'
                                                        data-symbol={t('currency.currency_symbol')}
                                                    >
                                                        {t('field.unit_price')}
                                                    </span>
                                                </th>
                                                <th className='border w-[10%] p-2'>{t('field.quantity')}</th>
                                                <th className='border  w-[10%] p-2'>{t('field.number_of_weeks')}</th>
                                                <th className='border w-[15%] p-2'>
                                                    <span
                                                        className='unit-price'
                                                        data-symbol={t('currency.currency_symbol')}
                                                    >
                                                        {t('field.unit_price')}
                                                    </span>
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {oldAdditionalServices.map((item) => {
                                                let unitPrice: number | null = null;

                                                let applyNumberOfWeek = [OPTION_FORMAT.Option.valueOf(), OPTION_FORMAT.Insurance.valueOf()].includes(Number(item.format));

                                                if (formatCurrency(item.fee)) {
                                                    unitPrice = Number(item.fee) / ((Number(item.quantity || 1)) * (applyNumberOfWeek ? Number(dateAndTime?.numberOfWeeks || 1) : 1));
                                                }
                                                return (
                                                    <tr key={item.id} className='border text-left'>
                                                        <td className='border p-2'>{item.name}</td>
                                                        <td className='border p-2 text-right'>{formatCurrency(unitPrice)}</td>
                                                        <td className='border p-2 text-center'>{item.quantity}</td>
                                                        <td className='border p-2 text-center'>{applyNumberOfWeek ? dateAndTime?.numberOfWeeks : null}</td>
                                                        <td className='border p-2 text-customRed text-right'>{formatCurrency(item.fee)}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                    <div className='flex justify-end text-primaryWeb font-semibold text-fs-mid16 w-full gap-1 mt-3'>
                                        {/* <label>Fee: </label> */}
                                        <p>
                                            {t('currency.currency_symbol')}
                                            {formatCurrency(oldTotalOptionFee) || '0'}
                                        </p>
                                    </div>
                                </td>
                                <td className='min-w-5'>
                                    <div className='w-20 h-2/3 flex items-start justify-center'>
                                        <div className='size-12 bg-customRed text-white rounded-full flex items-center justify-center'>
                                            <ChevronRight className='size-6' strokeWidth={2} />
                                        </div>
                                    </div>
                                </td>
                                <td className='align-top border-b p-3 min-h-full'>
                                    <table className='border border-collapse text-center w-full option-selected'>
                                        <thead>
                                            <tr className='bg-gray-5 text-black'>
                                                <th className='border w-1/2 p-2'>{t('field.option_page.option_name')}</th>
                                                <th className='border w-[15%] p-2'>
                                                    <span
                                                        className='unit-price'
                                                        data-symbol={t('currency.currency_symbol')}
                                                    >
                                                        {t('field.unit_price')}
                                                    </span>
                                                </th>
                                                <th className='border w-[10%] p-2'>{t('field.quantity')}</th>
                                                <th className='border  w-[10%] p-2'>{t('field.number_of_weeks')}</th>
                                                <th className='border w-[15%] p-2'>
                                                    <span
                                                        className='unit-price'
                                                        data-symbol={t('currency.currency_symbol')}
                                                    >
                                                        {t('field.unit_price')}
                                                    </span>
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {newAdditionalServices.map(item => (
                                                <tr key={item.id} className='border text-left'>
                                                    <td className='border p-2'>{item.name}</td>
                                                    <td className='border p-2 text-right'>{formatCurrency(item.price)}</td>
                                                    <td className='border p-2 text-center'>{item.value}</td>
                                                    <td className='border p-2 text-center'>{item.numberOfWeeks}</td>
                                                    <td className='border p-2 text-right text-customRed'>{formatCurrency(item.totalPrice)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    <div className='flex justify-end text-primaryWeb text-fs-mid16 font-semibold w-full gap-1 mt-3'>
                                        <p>
                                            {t('currency.currency_symbol')}
                                            {formatCurrency(newTotalOption) || '0'}
                                        </p>
                                    </div>
                                </td>
                            </tr>
                            <tr className=''>
                                <td className='border-b p-3 font-semibold whitespace-nowrap'>{t('field.coupon')}</td>
                                <td className='min-w-5 border-b'></td>
                                <td className='border-b p-3 font-semibold text-right'>
                                    &#9650;
                                    {t('currency.currency_symbol')}
                                    {formatCurrency(oldDiscount) || '0'}
                                </td>
                                <td className='min-w-5'></td>
                                <td className='border-b p-3 font-semibold text-right'>
                                    &#9650;
                                    {t('currency.currency_symbol')}
                                    {formatCurrency(totalCost?.coupon) || '0'}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    {/* <div className='w-20 flex items-center justify-center'>
                        <div className='size-12 bg-customRed text-white rounded-full flex items-center justify-center'>
                            <ChevronRight className='size-6' strokeWidth={2} />
                        </div>
                    </div>
                    <table className='border-collapse w-fit h-fit booking-table'>
                        <thead>
                            <tr>
                                <th className='bg-customRed p-3 text-white'>{t('words_title.after_change')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className=''>
                                <td className='border-b p-3'>
                                    <div className='flex gap-1'>
                                        <label>
                                            {t('field.rental_date_and_time')}
                                            :
                                        </label>
                                        <span>{newDateTime.startTime && formatDateTime(newDateTime.startTime, DISPPLAY_MOMENT_YMD_HM, { useLocalTime: false })}</span>
                                    </div>
                                    <div className='flex gap-1'>
                                        <label>
                                            {t('field.return_date_and_time')}
                                            :
                                        </label>
                                        <span>{newDateTime.endTime && formatDateTime(newDateTime.endTime, DISPPLAY_MOMENT_YMD_HM, { useLocalTime: false })}</span>
                                    </div>
                                </td>
                            </tr>
                            <tr className=''>
                                <td className='border-b p-3'>
                                    <div className='flex justify-between gap-2'>
                                        <p>{newLocation?.name}</p>
                                        <p className='text-black text-fs-mid16 font-semibold'>
                                            <span>
                                                {t('field.fee')}
                                                :
                                                {' '}
                                            </span>
                                            <span>
                                                {t('currency.currency_symbol')}
                                                {formatCurrency(dateAndTimeCost) || '0'}
                                            </span>
                                        </p>
                                    </div>
                                </td>
                            </tr>
                            <tr className=''>
                                <td className='border-b p-3'>{newBike.bikeModel}</td>
                            </tr>
                            <tr className=''>
                                <td className='align-top border-b p-3 min-h-full'>
                                    <table className='border border-collapse text-center w-full option-selected'>
                                        <thead>
                                            <tr className='bg-gray-5 text-black'>
                                                <th className='border w-1/2 p-2'>{t('field.option_page.option_name')}</th>
                                                <th className='border w-[15%] p-2'>
                                                    <span
                                                        className='unit-price'
                                                        data-symbol={t('currency.currency_symbol')}
                                                    >
                                                        {t('field.unit_price')}
                                                    </span>
                                                </th>
                                                <th className='border w-[10%] p-2'>{t('field.quantity')}</th>
                                                <th className='border  w-[10%] p-2'>{t('field.number_of_weeks')}</th>
                                                <th className='border w-[15%] p-2'>
                                                    <span
                                                        className='unit-price'
                                                        data-symbol={t('currency.currency_symbol')}
                                                    >
                                                        {t('field.unit_price')}
                                                    </span>
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {newAdditionalServices.map(item => (
                                                <tr key={item.id} className='border text-left'>
                                                    <td className='border p-2'>{item.name}</td>
                                                    <td className='border p-2 text-right'>{formatCurrency(item.price)}</td>
                                                    <td className='border p-2 text-center'>{item.value}</td>
                                                    <td className='border p-2 text-center'>{item.numberOfWeeks}</td>
                                                    <td className='border p-2 text-right text-customRed'>{formatCurrency(item.totalPrice)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    <div className='flex justify-end text-primaryWeb text-fs-mid16 font-semibold w-full gap-1 mt-3'>
                                        <p>
                                            {t('currency.currency_symbol')}
                                            {formatCurrency(newTotalOption) || '0'}
                                        </p>
                                    </div>
                                </td>
                            </tr>
                            <tr className=''>
                                <td className='border-b p-3 font-semibold text-right'>
                                    &#9650;
                                    {t('currency.currency_symbol')}
                                    {formatCurrency(totalCost?.coupon) || '0'}
                                </td>
                            </tr>
                        </tbody>
                    </table> */}
                </div>
                <div className='flex gap-2 items-center'>
                    <Checkbox
                        checked={isCheckSendMail}
                        onCheckedChange={checked => setCheckSendMail(Boolean(checked.valueOf()))}
                    />
                    <Label className='font-normal'>{t('sentences.send_mail_to_user')}</Label>
                </div>
            </div>
            <div className='flex justify-center text-[0.875rem] gap-2 md:gap-4'>
                <ButtonCus type='button' onClick={() => backToPrev()} variant='outline' size='sm'>{t('button.back')}</ButtonCus>
                <ButtonCus type='submit' onClick={() => onSubmit()} variant='default' size='sm'>{t('button.save')}</ButtonCus>
            </div>
        </div>
    );
});
